import { InfoScreenData } from '../types/InfoScreenData';
import { CalendarPeriod } from '../school-calendar/school-calendar.types';

/**
 * What the standard screen (the old address, `?locationId=`) shows, chosen in the address with
 * `view`: `list` (the default), `day` (the school timetable for today, the resources as columns)
 * or `week` (the week, the weekdays as columns). The Danish words work too: `liste`, `dag`, `uge`.
 * Anything else is the list. No module needed — the open endpoint serves all three.
 */
export type LegacyView = 'list' | 'day' | 'week';

const VIEWS: Record<string, LegacyView> = {
  list: 'list',
  liste: 'list',
  day: 'day',
  dag: 'day',
  week: 'week',
  uge: 'week',
};

export function legacyView(param: unknown): LegacyView {
  return (typeof param === 'string' && VIEWS[param.trim().toLowerCase()]) || 'list';
}

/** The days the view fetches: today, or Monday to Monday of this week. */
export function legacyRange(view: LegacyView, now: Date): { from: Date; to: Date } {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (view !== 'week') return { from: today, to: addDays(today, 1) };
  const monday = addDays(today, -((today.getDay() + 6) % 7));
  return { from: monday, to: addDays(monday, 7) };
}

/**
 * The old endpoint's bookings as the school timetable's periods. It sends no resource id, so a
 * resource is told apart by its name and its parent's ("Hal 1 / Bane 2"), and gets an id made from
 * them — the same in every fetch, whatever the order of the answer, so the day's columns keep their
 * keys from one minute's refresh to the next.
 */
export function toPeriods(values: InfoScreenData[]): CalendarPeriod[] {
  return values.map((value) => ({ ...value, resourceId: resourceKey(value.resourceParent, value.resource) }));
}

/** A whole number from the parent's and the resource's names (FNV-1a), the same for the same pair. */
export function resourceKey(parent: string | null | undefined, name: string | null | undefined): number {
  let hash = 0x811c9dc5;
  for (const char of `${parent ?? ''}|${name ?? ''}`) {
    hash ^= char.codePointAt(0)!;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

/** A local date and time the API reads as the tenant's own ("2026-10-12T00:00:00"). */
export function localIso(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}:00`;
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}
